/* Sri Vibhinna — theme behaviour.
 *
 * Everything here is an enhancement. No section depends on this file to
 * display its content: sheets are <dialog> elements with real links inside,
 * add-to-bag is a real form POST, and rows scroll natively. If this script
 * never loads, the store still works.
 *
 * Vanilla, no dependencies, no build step.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Sheets (menu, categories) ---------------------------------- */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-sheet-open]');
    if (opener) {
      var sheet = document.getElementById(opener.getAttribute('data-sheet-open'));
      if (sheet && typeof sheet.showModal === 'function') {
        e.preventDefault();
        sheet.showModal();
        opener.setAttribute('aria-expanded', 'true');
        sheet.__opener = opener;
      }
      return;
    }

    var closer = e.target.closest('[data-sheet-close]');
    if (closer) {
      var open = closer.closest('dialog');
      if (open) open.close();
      return;
    }

    // Clicking the backdrop closes the sheet.
    if (e.target.tagName === 'DIALOG' && e.target.open) e.target.close();
  });

  document.querySelectorAll('dialog').forEach(function (sheet) {
    sheet.addEventListener('close', function () {
      if (sheet.__opener) {
        sheet.__opener.setAttribute('aria-expanded', 'false');
        sheet.__opener.focus();
      }
    });
  });

  /* ---------- Header lift on scroll -------------------------------------- */
  var header = document.querySelector('[data-header]');
  if (header) {
    var onScroll = function () {
      if (window.scrollY > 8) header.setAttribute('data-scrolled', '');
      else header.removeAttribute('data-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Add to bag -------------------------------------------------
   * Progressive: the form posts natively without this. With it, we add in the
   * background and update the bag count, so the shopper keeps her place in
   * the row she was browsing.
   * -------------------------------------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    // The sticky bar's button lives outside the form and points at it with
    // `form=`, so trust the submitter when the browser tells us who it was.
    var button =
      (e.submitter && e.submitter.matches('[data-add-to-bag]') && e.submitter) ||
      form.querySelector('[data-add-to-bag]');
    if (!button) return;

    e.preventDefault();
    var label = button.querySelector('[data-add-label]');
    var rest = label ? label.textContent : '';
    if (label) label.textContent = 'Adding…';
    button.disabled = true;

    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || 'Could not add this piece.');
          return data;
        });
      })
      .then(function () {
        var moving = form.getAttribute('data-wish-move');
        if (label) label.textContent = moving ? 'Moved to bag' : 'Added';
        refreshCartCount();
        // The wishlist listens for this to finish a "Move to bag".
        form.dispatchEvent(new CustomEvent('sv:added-to-bag', { bubbles: true }));
        setTimeout(function () {
          if (label) label.textContent = rest;
          button.disabled = false;
        }, 1200);
      })
      .catch(function (err) {
        button.disabled = false;
        if (label) label.textContent = rest;
        showCardError(form, err.message);
      });
  });

  function showCardError(form, message) {
    var holder = form.parentNode;
    var existing = holder.querySelector('[data-add-error]');
    if (existing) existing.remove();
    var p = document.createElement('p');
    p.className = 't-meta';
    p.setAttribute('data-add-error', '');
    p.setAttribute('role', 'status');
    p.style.color = 'var(--c-maroon)';
    p.style.marginTop = 'var(--s-2)';
    p.textContent = message;
    holder.appendChild(p);
  }

  function refreshCartCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () { /* the header count simply stays as it was */ });
  }

  /* ---------- Quantity steppers -----------------------------------------
   * Used by the product page and the cart. The input is a real number field
   * that already works with a keyboard and without this handler; the buttons
   * exist because a 44px target beats a spinner arrow on a phone.
   * -------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var step = e.target.closest('[data-step]');
    if (!step) return;
    var input = step.parentNode.querySelector('input');
    if (!input) return;
    var min = parseInt(input.getAttribute('min') || '1', 10);
    var next = (parseInt(input.value, 10) || min) + parseInt(step.getAttribute('data-step'), 10);
    input.value = Math.max(min, next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart quantity edits ---------------------------------------
   * A quantity change submits the cart form — the same endpoint the no-JS
   * "Update quantities" button posts to, one tap sooner.
   * -------------------------------------------------------------------- */
  var cartForm = document.querySelector('[data-cart-form]');
  if (cartForm) {
    var pending;
    cartForm.addEventListener('change', function (e) {
      if (!e.target.matches('[data-qty]')) return;
      clearTimeout(pending);
      pending = setTimeout(function () { cartForm.submit(); }, 320);
    });
  }

  /* ---------- Product gallery -------------------------------------------
   * The gallery is a native scroll-snap strip, so it swipes with no JS at
   * all. This keeps the thumbnail rail and the screen-reader status in step,
   * and makes each thumbnail a jump control.
   * -------------------------------------------------------------------- */
  var gallery = document.querySelector('[data-gallery]');
  var thumbWrap = document.querySelector('[data-gallery-thumbs]');
  if (gallery && thumbWrap) {
    var thumbs = thumbWrap.querySelectorAll('[data-thumb]');
    var slides = gallery.children;
    var galleryStatus = document.querySelector('[data-gallery-status]');
    var snapBehavior = reduceMotion ? 'auto' : 'smooth';

    gallery.addEventListener(
      'scroll',
      function () {
        var i = Math.round(gallery.scrollLeft / gallery.clientWidth);
        for (var d = 0; d < thumbs.length; d++) {
          thumbs[d].classList.toggle('is-on', d === i);
          if (d === i) thumbs[d].setAttribute('aria-current', 'true');
          else thumbs[d].removeAttribute('aria-current');
        }
        if (galleryStatus) {
          galleryStatus.textContent = 'Photograph ' + (i + 1) + ' of ' + thumbs.length;
        }
        // Keep the active thumb in view without ever scrolling the page.
        var t = thumbs[i];
        if (t) {
          var wrapRect = thumbWrap.getBoundingClientRect();
          var tRect = t.getBoundingClientRect();
          thumbWrap.scrollTo({
            left: thumbWrap.scrollLeft + (tRect.left - wrapRect.left) - (wrapRect.width - tRect.width) / 2,
            behavior: snapBehavior
          });
        }
      },
      { passive: true }
    );

    thumbWrap.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-thumb]');
      if (!btn) return;
      var i = Array.prototype.indexOf.call(thumbs, btn);
      if (i < 0 || !slides[i]) return;
      var gRect = gallery.getBoundingClientRect();
      var sRect = slides[i].getBoundingClientRect();
      gallery.scrollTo({
        left: gallery.scrollLeft + (sRect.left - gRect.left),
        behavior: snapBehavior
      });
    });
  }

  /* ---------- Spotlight carousels ----------------------------------------
   * Motion move #5 (DESIGN §8): content motion under the reel discipline.
   * The strip is a native scroll-snap row that works fully without this
   * script; this adds the centre highlight, the desktop chevrons, and the
   * automatic turn — which runs only while the section is on screen, stops
   * whenever attention lands on it (hover, touch, focus), and never runs at
   * all under reduced motion.
   * -------------------------------------------------------------------- */
  document.querySelectorAll('[data-spotlight]').forEach(function (spot) {
    var track = spot.querySelector('[data-spot-track]');
    if (!track || track.children.length < 2) return;

    /* Seamless cycle: clone the two outermost cards onto each end, and when
     * the strip settles on a clone, teleport (no animation) to its real
     * twin. Clones are scenery — hidden from the accessibility tree, ids
     * stripped, nothing in them focusable. Without JS no clones exist and
     * the strip is an ordinary finite row.
     */
    var realCount = track.children.length;
    var cloneCount = Math.min(3, realCount);
    var neuter = function (node) {
      node.setAttribute('aria-hidden', 'true');
      node.setAttribute('data-spot-clone', '');
      node.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
      node.querySelectorAll('a, button, input, select, [tabindex]').forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
      return node;
    };
    var originals = Array.prototype.slice.call(track.children);
    for (var cA = 0; cA < cloneCount; cA++) {
      track.appendChild(neuter(originals[cA].cloneNode(true)));
    }
    for (var cB = 0; cB < cloneCount; cB++) {
      track.insertBefore(neuter(originals[realCount - 1 - cB].cloneNode(true)), track.firstChild);
    }
    var items = track.children;

    /* Rect-based like every other measurement here: offsetLeft is measured
       from the positioned ancestor, not the track, and once the track
       bleeds to the viewport that origin error grows to a whole card —
       highlight lands off-centre, "advance one" skips two, the wrap pairs
       the wrong twins. */
    var nearest = function () {
      var tr = track.getBoundingClientRect();
      var mid = tr.left + tr.width / 2;
      var best = 0;
      var bestD = Infinity;
      for (var i = 0; i < items.length; i++) {
        var r = items[i].getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - mid);
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    };

    var mark = function () {
      var best = nearest();
      for (var j = 0; j < items.length; j++) {
        items[j].classList.toggle('is-center', j === best);
      }
      return best;
    };

    /* All positioning is relative — a delta from where the strip is right
     * now — never an absolute offsetLeft. Absolute targets were measured
     * from the wrong origin (offsetParent is the section, not the track),
     * landed a hair off the snap point, and mandatory snap then visibly
     * corrected them: the "bounce". A delta cannot miss.
     */
    var centerDelta = function (i) {
      var tr = track.getBoundingClientRect();
      var ir = items[i].getBoundingClientRect();
      return ir.left + ir.width / 2 - (tr.left + tr.width / 2);
    };

    var goTo = function (i, instant) {
      i = Math.max(0, Math.min(items.length - 1, i));
      if (instant || reduceMotion) track.scrollLeft += centerDelta(i);
      else track.scrollBy({ left: centerDelta(i), behavior: 'smooth' });
    };

    /* A settled position on a clone becomes its real twin. The twins sit
       exactly one catalogue-length apart in layout, so the jump preserves
       the snap alignment to the pixel. The centre state moves with it under
       suspended transitions — the twin must APPEAR enlarged, not enlarge
       again, or the wrap plays the grow animation twice. */
    var settleT;
    var settle = function () {
      var i = nearest();
      if (!items[i].hasAttribute('data-spot-clone')) return;
      // Never teleport mid-flight: a jump while a smooth scroll is still
      // running leaves the old animation fighting the new position — the
      // visible stutter. Only a clone at true rest, dead centre, swaps.
      if (Math.abs(centerDelta(i)) > 2) return;
      var real = i < cloneCount ? i + realCount : i - realCount;
      track.classList.add('spot__track--warp');
      track.scrollLeft += items[real].offsetLeft - items[i].offsetLeft;
      mark();
      void track.offsetWidth; // commit the swapped states before motion returns
      requestAnimationFrame(function () {
        track.classList.remove('spot__track--warp');
      });
    };

    /* `scrollend` fires once scrolling — snap included — has genuinely
       finished; the timer is only the fallback for browsers without it,
       and the centre guard above covers its guesswork. */
    var hasScrollEnd = 'onscrollend' in window;
    if (hasScrollEnd) track.addEventListener('scrollend', settle);

    track.addEventListener(
      'scroll',
      function () {
        requestAnimationFrame(mark);
        if (!hasScrollEnd) {
          clearTimeout(settleT);
          settleT = setTimeout(settle, 250);
        }
      },
      { passive: true }
    );

    /* Open on the SECOND piece, so the stage has wings on both sides from
       the first paint. */
    goTo(cloneCount + Math.min(1, realCount - 1), true);
    mark();

    spot.querySelectorAll('[data-spot-prev], [data-spot-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        settle();
        goTo(nearest() + (btn.hasAttribute('data-spot-next') ? 1 : -1));
      });
    });

    var speed = parseInt(spot.getAttribute('data-rotate') || '0', 10) * 1000;
    if (!speed || reduceMotion) return;

    var visible = false;
    var engaged = false;
    var timer = null;

    var sync = function () {
      if (visible && !engaged) {
        if (!timer) {
          timer = setTimeout(function () {
            timer = null;
            settle();
            goTo(nearest() + 1);
            sync();
          }, speed);
        }
      } else if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    spot.addEventListener('mouseenter', function () { engaged = true; sync(); });
    spot.addEventListener('mouseleave', function () { engaged = false; sync(); });
    spot.addEventListener('focusin', function () { engaged = true; sync(); });
    spot.addEventListener('focusout', function (e) {
      if (!spot.contains(e.relatedTarget)) { engaged = false; sync(); }
    });
    spot.addEventListener('touchstart', function () { engaged = true; sync(); }, { passive: true });
    spot.addEventListener('touchend', function () {
      // A beat of reading time before the turn resumes.
      setTimeout(function () { engaged = false; sync(); }, 1500);
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            visible = entry.isIntersecting;
            sync();
          });
        },
        { threshold: 0.4 }
      ).observe(spot);
    } else {
      visible = true;
      sync();
    }
  });

  /* ---------- Recently viewed --------------------------------------------
   * The trail lives only in this browser (localStorage) — never sent
   * anywhere, expiring on its own. The shelf ships `hidden` and is revealed
   * only when at least two pieces survive the exclusions, so its empty
   * state is invisibility, never a hole. Cards come from the Section
   * Rendering API so they are the real product-card snippet.
   * -------------------------------------------------------------------- */
  (function () {
    var KEY = 'sv:recently-viewed';
    var KEEP = 12;
    var TTL = 30 * 24 * 60 * 60 * 1000; // 30 days
    var now = Date.now();

    var trail = [];
    try {
      trail = JSON.parse(localStorage.getItem(KEY) || '[]').filter(function (e) {
        return e && e.h && now - (e.t || 0) < TTL;
      });
    } catch (e) { /* blocked storage: no trail, no shelf */ }

    var shelf = document.querySelector('[data-recently-viewed]');
    var current = shelf && shelf.getAttribute('data-current-handle');

    // Record before rendering: the current piece joins the trail even though
    // it is never shown on its own page.
    if (current) {
      trail = trail.filter(function (e) { return e.h !== current; });
      trail.unshift({ h: current, t: now });
      try { localStorage.setItem(KEY, JSON.stringify(trail.slice(0, KEEP))); } catch (e) {}
    }

    var track = shelf && shelf.querySelector('[data-recent-track]');
    if (!shelf || !track) return;

    // The trail owns its pieces (owner's call, 2026-09-07): recently viewed
    // shows the trail in full, and the pieces it shows are then REMOVED from
    // the wider "You may also like" row above, which backfills the page from
    // the rest of the shop. Only the piece on the counter is excluded here.
    var max = parseInt(shelf.getAttribute('data-max') || '6', 10);
    var handles = [];
    trail.forEach(function (e) {
      if (e.h !== current && handles.length < max) handles.push(e.h);
    });
    if (handles.length < 2) return;

    Promise.all(
      handles.map(function (h) {
        return fetch('/products/' + h + '?section_id=recently-viewed-card')
          .then(function (r) { return r.ok ? r.text() : null; })
          .catch(function () { return null; });
      })
    ).then(function (parts) {
      var cards = [];
      var shownHandles = {};
      parts.forEach(function (html, i) {
        if (!html) return; // deleted or hidden piece: dropped silently
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var card = holder.querySelector('.card');
        if (card) {
          var cell = document.createElement('div');
          cell.appendChild(card);
          cards.push(cell);
          shownHandles[handles[i]] = true;
        }
      });
      if (cards.length < 2) return; // still a thin shelf: stay invisible
      cards.forEach(function (cell) { track.appendChild(cell); });
      shelf.hidden = false;
      // Cards that arrive after load need their wishlist hearts painted.
      document.dispatchEvent(new CustomEvent('sv:cards-added'));

      // Now dedupe the other way: pull the trail's pieces out of the
      // related row so nothing sits on the page twice. Done only at reveal,
      // so a failed shelf never silently removes pieces from the page.
      var related = document.querySelector('.product-row');
      if (!related) return;
      Array.prototype.slice.call(related.children).forEach(function (cell) {
        var link = cell.matches('a[href*="/products/"]')
          ? cell
          : cell.querySelector('a[href*="/products/"]');
        var m = link && (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
        if (m && shownHandles[m[1]]) related.removeChild(cell);
      });
      if (!related.querySelector('.card')) {
        var band = related.closest('.band');
        if (band) band.hidden = true;
      }
    });
  })();

  /* ---------- Share ------------------------------------------------------
   * The native share sheet where the platform has one; a copied link where it
   * doesn't. Either way the URL is the product's canonical one, not whatever
   * query string the visitor arrived with.
   * -------------------------------------------------------------------- */
  var shareBtn = document.querySelector('[data-share]');
  if (shareBtn) {
    shareBtn.addEventListener('click', function () {
      var url = shareBtn.getAttribute('data-share-url') || location.href;
      var title = shareBtn.getAttribute('data-share-title') || document.title;
      if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(function () {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(function () {
          var note = document.querySelector('[data-share-note]');
          if (note) {
            note.hidden = false;
            setTimeout(function () { note.hidden = true; }, 2400);
          }
        }).catch(function () {});
      }
    });
  }

  /* ---------- Sticky add to bag -----------------------------------------
   * Shown whenever the real button is off screen — including before the
   * first scroll, so a buy control is visible from the moment the page
   * loads. It never duplicates a control already on screen, and it submits
   * that same form rather than owning a second add-to-bag path.
   * -------------------------------------------------------------------- */
  var atcBar = document.querySelector('[data-atc-bar]');
  var atcAnchor = document.querySelector('[data-atc-anchor]');
  if (atcBar && atcAnchor && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          atcBar.hidden = entry.isIntersecting;
        });
      },
      { rootMargin: '0px 0px -80px 0px' }
    ).observe(atcAnchor);
  }

  /* ---------- Reels ------------------------------------------------------
   * Play only while on screen, and never without asking under reduced motion:
   * those visitors get the poster and a play control instead.
   * -------------------------------------------------------------------- */
  var reels = document.querySelectorAll('[data-reel]');
  if (reels.length && 'IntersectionObserver' in window) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting && !reduceMotion) {
            var p = video.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    reels.forEach(function (reel) {
      io.observe(reel);
      var video = reel.querySelector('video');
      var play = reel.querySelector('[data-reel-play]');
      var sound = reel.querySelector('[data-reel-sound]');
      if (!video) return;

      if (play) {
        play.hidden = !reduceMotion;
        video.addEventListener('playing', function () { play.hidden = true; });
        video.addEventListener('pause', function () { if (reduceMotion) play.hidden = false; });
        play.addEventListener('click', function () {
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
        });
      }

      if (sound) {
        sound.addEventListener('click', function () {
          video.muted = !video.muted;
          sound.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
          var use = sound.querySelector('use');
          if (use) use.setAttribute('href', video.muted ? '#sv-sound-off' : '#sv-sound-on');
        });
      }

      // Tapping the video itself toggles sound, matching how reels behave
      // everywhere else. The product bar keeps its own click.
      video.addEventListener('click', function () {
        if (sound) sound.click();
      });
    });
  }

  /* ---------- Rich text: make pasted tables scroll ------------------------
   * The merchant writes policy and About pages in Shopify's editor, so we
   * never see the markup. A table pasted in there is the one thing that can
   * push a phone layout wider than its viewport. Wrapping it lets the table
   * scroll inside itself instead of scrolling the page. Progressive: with no
   * JS the table simply behaves as it did.
   */
  document.querySelectorAll('.rte table').forEach(function (table) {
    if (table.parentElement && table.parentElement.classList.contains('rte__scroll')) return;
    var wrap = document.createElement('div');
    wrap.className = 'rte__scroll';
    // Scrollable regions must be keyboard-reachable (WCAG 2.1.1).
    wrap.setAttribute('tabindex', '0');
    wrap.setAttribute('role', 'region');
    wrap.setAttribute('aria-label', table.caption ? table.caption.textContent : 'Table');
    table.parentNode.insertBefore(wrap, table);
    wrap.appendChild(table);
  });

  /*
   * Announcement bar: the pause button WCAG 2.2.2 asks for on anything that
   * moves for more than five seconds. Hover and focus already pause it in CSS;
   * this makes the stop stick for touch, where there is no hover.
   */
  /*
   * Glide at a fixed pace in pixels per second. A fixed duration would make
   * the speed depend on how long the strip is (it repeats its messages to fill
   * wide screens), which is how it ended up too fast to read.
   */
  document.querySelectorAll('[data-announce-pace]').forEach(function (track) {
    var group = track.firstElementChild;
    var pace = parseFloat(track.getAttribute('data-announce-pace')) || 30;
    var set = function () {
      if (group && group.offsetWidth) {
        track.style.setProperty('--announce-duration', (group.offsetWidth / pace).toFixed(1) + 's');
      }
    };
    set();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(set);
  });

  document.querySelectorAll('[data-announce-pause]').forEach(function (btn) {
    var bar = btn.closest('[data-announce]');
    var label = btn.querySelector('.visually-hidden');
    var use = btn.querySelector('use');
    btn.addEventListener('click', function () {
      var paused = bar.classList.toggle('announce--paused');
      btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      if (label) label.textContent = paused ? 'Play the moving announcements' : 'Pause the moving announcements';
      if (use) use.setAttribute('href', paused ? '#sv-play' : '#sv-pause');
    });
  });

  /* ---------- Wishlist (DESIGN §5.20) -----------------------------------
   * Saved pieces live only in this browser (localStorage), like the
   * recently-viewed trail: nothing is sent anywhere and no account is
   * needed. Every heart ships `hidden` and is revealed here, so without
   * JS — or with storage blocked — there is no control that does nothing.
   * The sheet shows the REAL product card per piece (Section Rendering API,
   * sections/wishlist-card), so prices and sold-out states are live.
   * -------------------------------------------------------------------- */
  (function () {
    var KEY = 'sv:wishlist';
    var MAX = 60;

    var read = function () {
      try {
        var saved = JSON.parse(localStorage.getItem(KEY) || '[]');
        return Array.isArray(saved) ? saved.filter(function (e) { return e && e.h; }) : [];
      } catch (e) {
        return null;
      }
    };

    var list = read();
    if (list === null) return; // storage blocked: hearts stay hidden

    var write = function () {
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
    };
    var has = function (h) {
      return list.some(function (e) { return e.h === h; });
    };

    var listEl = document.querySelector('[data-wish-list]');
    var emptyEl = document.querySelector('[data-wish-empty]');

    var paint = function () {
      document.querySelectorAll('[data-wish]').forEach(function (btn) {
        var on = has(btn.getAttribute('data-wish'));
        btn.hidden = false;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var use = btn.querySelector('use');
        if (use) use.setAttribute('href', on ? '#sv-heart-fill' : '#sv-heart');
      });
      document.querySelectorAll('[data-wish-from-cart]').forEach(function (btn) {
        btn.hidden = false;
      });
      document.querySelectorAll('[data-wish-open]').forEach(function (open) {
        open.hidden = false;
        var label = open.querySelector('.visually-hidden');
        if (label) label.textContent = list.length ? 'Wishlist, ' + list.length + ' saved' : 'Wishlist';
      });
      document.querySelectorAll('[data-wish-count]').forEach(function (count) {
        count.textContent = list.length;
        count.hidden = list.length === 0;
      });
    };

    var showEmpty = function () {
      if (listEl) listEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
    };

    var render = function () {
      if (!listEl) return;
      listEl.innerHTML = '';
      if (!list.length) return showEmpty();
      if (emptyEl) emptyEl.hidden = true;
      listEl.setAttribute('aria-busy', 'true');

      var handles = list.map(function (e) { return e.h; });
      Promise.all(
        handles.map(function (h) {
          return fetch('/products/' + encodeURIComponent(h) + '?section_id=wishlist-card')
            .then(function (r) {
              if (!r.ok) return { status: r.status, html: null };
              return r.text().then(function (html) { return { status: r.status, html: html }; });
            })
            .catch(function () { return { status: 0, html: null }; });
        })
      ).then(function (parts) {
        var gone = {};
        parts.forEach(function (part, i) {
          // A piece the shop deleted (404) leaves the list; a network blip
          // does not, so a flaky connection never empties anyone's wishlist.
          if (part.status === 404) { gone[handles[i]] = true; return; }
          if (!part.html) return;
          var holder = document.createElement('div');
          holder.innerHTML = part.html;
          var card = holder.querySelector('.card');
          if (!card) return;
          var cell = document.createElement('div');
          cell.appendChild(card);
          listEl.appendChild(cell);
        });
        if (Object.keys(gone).length) {
          list = list.filter(function (e) { return !gone[e.h]; });
          write();
        }
        listEl.removeAttribute('aria-busy');
        paint();
        if (!listEl.children.length) showEmpty();
      });
    };

    document.addEventListener('click', function (e) {
      // "Move to wishlist" on the bag page: save first, then leave through
      // the line's own remove URL, so the bag reloads exactly as Remove does.
      var fromCart = e.target.closest('[data-wish-from-cart]');
      if (fromCart) {
        var hc = fromCart.getAttribute('data-wish-from-cart');
        if (!has(hc)) list.unshift({ h: hc, t: Date.now() });
        write();
        paint();
        fromCart.disabled = true;
        fromCart.textContent = 'Moved to wishlist';
        window.location.href = fromCart.getAttribute('data-remove-url');
        return;
      }

      if (e.target.closest('[data-wish-open]')) {
        render(); // the generic sheet handler opens the dialog
        return;
      }

      var btn = e.target.closest('[data-wish]');
      if (!btn) return;
      e.preventDefault();
      var h = btn.getAttribute('data-wish');
      if (has(h)) {
        list = list.filter(function (x) { return x.h !== h; });
      } else {
        list.unshift({ h: h, t: Date.now() });
      }
      write();
      paint();

      // Un-hearting a piece inside the open sheet takes its card out too.
      var cell = listEl && btn.closest('[data-wish-list] > *');
      if (cell && !has(h)) {
        cell.remove();
        if (!listEl.children.length) showEmpty();
      }
    });

    // Another tab changed the list; keep this one honest.
    window.addEventListener('storage', function (e) {
      if (e.key !== KEY) return;
      list = read() || [];
      paint();
    });

    // "Move to bag": once the add has succeeded, the piece leaves the
    // wishlist. A failed add leaves it where it was.
    document.addEventListener('sv:added-to-bag', function (e) {
      var h = e.target.getAttribute('data-wish-move');
      if (!h) return;
      list = list.filter(function (x) { return x.h !== h; });
      write();
      paint();
      var cell = listEl && e.target.closest('[data-wish-list] > *');
      if (cell) {
        // Long enough to read "Moved to bag" before the card goes.
        setTimeout(function () {
          cell.remove();
          if (!listEl.children.length) showEmpty();
        }, 900);
      }
    });

    document.addEventListener('sv:cards-added', paint);
    paint();
  })();
})();
